import { COMPONENT_NAME_KEYWORD, COMPONENT_LABEL_KEYWORD, COMPONENT_IDENTIFIER_KEYWORD, BARRED_DUPLICATE_COMPONENTS } from '../../lib';
import { useDynamicEntityStoreContext } from '../../contexts';
import { convertSnakeCaseToReadable } from '../../../utils/StringFormatters';

import ComponentPreview from './ComponentPreview';

function ComponentButton({ c, isSelected, handleSelectComponent, handleUnselectComponent, handleAddComponent, hasNoLayoutPreview }) {
  const { component_category, component_origin, description } = c;
  const content = useDynamicEntityStoreContext((state) => state.data.content);

  const isBarredComponent = BARRED_DUPLICATE_COMPONENTS.includes(c[COMPONENT_IDENTIFIER_KEYWORD]);
  const isAlreadyAdded = isBarredComponent && content.some((content) => BARRED_DUPLICATE_COMPONENTS.includes(content[COMPONENT_IDENTIFIER_KEYWORD]));

  const label = c[COMPONENT_LABEL_KEYWORD];
  const identifier = c[COMPONENT_IDENTIFIER_KEYWORD];
  const name = c[COMPONENT_NAME_KEYWORD];

  return (
    <div className="component-selection-container">
      <div
        className={`content ${isSelected ? 'selected' : ''}`}
        onClick={handleSelectComponent}
        onMouseLeave={handleUnselectComponent}
        disabled={isAlreadyAdded}
      >
        {isAlreadyAdded && <span className="button-overlay">You can only have one: {label || identifier || name}</span>}
        {isSelected && (
          <div className="button-overlay">
            <button
              className="button"
              type="button"
              data-bs-toggle="modal"
              data-bs-target={`#componentModal-${identifier}`}
              onClick={(e) => e.stopPropagation()}
            >
              More
            </button>
            <div className="vertical-rule" style={{ height: '90%' }}>
              <div className="vr" />
            </div>
            <button className="button" type="button" onClick={handleAddComponent}>
              Add
            </button>
          </div>
        )}
        {hasNoLayoutPreview && (
          <div className="preview">
            <ComponentPreview component={c} />
          </div>
        )}
        <div className="body">
          <span className={!label && !identifier ? 'text-danger' : ''}>
            {label || identifier || `${name} (Please change this to use the '${COMPONENT_IDENTIFIER_KEYWORD}' key)`}
          </span>
          <span className="badge text-muted">{convertSnakeCaseToReadable(component_category)}</span>
        </div>
      </div>

      {/* Bootstrap Modal */}
      <div className="modal fade" id={`componentModal-${identifier}`} tabIndex="-1" aria-labelledby={`componentModalLabel-${identifier}`} aria-hidden="true">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id={`componentModalLabel-${identifier}`}>
                {label || name}
              </h5>
              <button className="btn btn-danger btn-sm" type="button" data-bs-dismiss="modal" aria-label="Close">
                <i className="fe fe-x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="d-flex flex-column gap-5" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="d-flex flex-column gap-3 me-3">
                  <div className="d-flex flex-column">
                    <h3>Description</h3>
                    <p className="text-muted">{description || 'No description provided.'}</p>
                  </div>

                  <div className="d-flex flex-column gap-3">
                    <h3>Component Name</h3>
                    <pre>{JSON.stringify(identifier || name, null, 2)}</pre>
                  </div>

                  <div className="d-flex flex-column gap-3">
                    <h3>Component Label</h3>
                    <pre>{JSON.stringify(label, null, 2)}</pre>
                  </div>

                  <div className="d-flex flex-column gap-3">
                    <h3>Component Category</h3>
                    <pre>{JSON.stringify(convertSnakeCaseToReadable(component_category), null, 2)}</pre>
                  </div>

                  <div className="d-flex flex-column gap-3">
                    <h3>Component Origin</h3>
                    <pre>{JSON.stringify(convertSnakeCaseToReadable(component_origin), null, 2)}</pre>
                  </div>
                </div>
                <div className="d-flex flex-column gap-3 me-3">
                  <h3>Props</h3>
                  {c.propSchema
                    ? c.propSchema.map((prop, index) => {
                        if (!prop)
                          return (
                            <div className="dp-prop-card text-danger" key={index}>
                              This prop is not defined properly. It is either null or undefined or some other invalid data type.
                            </div>
                          );

                        const { name, label, type, defaultValue, options, object_schema, template_schema } = prop;

                        return (
                          <div className="d-flex flex-column gap-2 mb-0" key={index}>
                            <div className="dp-prop-card">
                              <div className="d-flex flex-column gap-3">
                                <span className="text-muted">Name</span>
                                <pre>{JSON.stringify(name, null, 2)}</pre>
                              </div>
                              <div className="d-flex flex-column gap-3">
                                <span className="text-muted">Label</span>
                                <pre>{JSON.stringify(label, null, 2)}</pre>
                              </div>
                              <div className="d-flex flex-column gap-3">
                                <span className="text-muted">Type</span>
                                <pre>{JSON.stringify(type, null, 2)}</pre>
                              </div>
                              <div className="d-flex flex-column gap-3">
                                <span className="text-muted">Default Value</span>
                                <pre>{JSON.stringify(defaultValue, null, 2)}</pre>
                              </div>
                              {options && (
                                <div className="d-flex flex-column gap-2">
                                  <span className="text-muted">Options</span>
                                  <pre className="text-break">{JSON.stringify(options, null, 2)}</pre>
                                </div>
                              )}
                              {object_schema && (
                                <div className="d-flex flex-column gap-2">
                                  <span className="text-muted">Object Schema</span>
                                  <pre className="text-break">{JSON.stringify(object_schema, null, 2)}</pre>
                                </div>
                              )}
                              {template_schema && (
                                <div className="d-flex flex-column gap-2">
                                  <span className="text-muted">Template Schema</span>
                                  <pre className="text-break">{JSON.stringify(template_schema, null, 2)}</pre>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    : null}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComponentButton;
